import { Content } from '../features/contents/contentsApi';

export const DEFAULT_PLACEHOLDER = '/logo.png';

/**
 * Extracts the best available pre-signed URL from a Content object.
 * Prioritizes the main signedUrl, then falls back to thumbnails or the raw URL.
 */
export const getSafeSignedUrl = (content: Content): string => {
  if (!content) return '';

  // 1. Prioritize the main pre-signed URL (full resolution)
  if (content.signedUrl && content.signedUrl.trim() !== '') {
    return content.signedUrl;
  }

  // 2. Fallback to the largest available signed thumbnail for items (like templates) 
  // that might not have a main signedUrl but have pre-signed thumbnails.
  if (content.thumbnailSignedUrls) {
    if (content.thumbnailSignedUrls.px_600x400 && content.thumbnailSignedUrls.px_600x400.trim() !== '') {
      return content.thumbnailSignedUrls.px_600x400;
    }
    if (content.thumbnailSignedUrls.px_50x50 && content.thumbnailSignedUrls.px_50x50.trim() !== '') {
      return content.thumbnailSignedUrls.px_50x50;
    }
  }

  // 3. Fallback to unsigned URL as last resort
  return content.url || '';
};

/**
 * Extracts a signed thumbnail URL optimized for list views.
 */
export const getSafeThumbnailUrl = (content: Content, preferredSize: 'px_50x50' | 'px_600x400' = 'px_600x400'): string => {
  if (!content) return '';

  const { thumbnailSignedUrls, signedUrl, thumbnail, url } = content;

  // 1. Try preferred size signed thumbnail
  if (thumbnailSignedUrls?.[preferredSize] && thumbnailSignedUrls[preferredSize]!.trim() !== '') {
    return thumbnailSignedUrls[preferredSize]!;
  }

  // 2. Try any other signed thumbnail size
  const otherSize = preferredSize === 'px_600x400' ? 'px_50x50' : 'px_600x400';
  if (thumbnailSignedUrls?.[otherSize] && thumbnailSignedUrls[otherSize]!.trim() !== '') {
    return thumbnailSignedUrls[otherSize]!;
  }

  // 3. Try main signed full-res URL (common for user images that might lack some thumb sizes)
  if (signedUrl && signedUrl.trim() !== '') {
    return signedUrl;
  }

  // 4. Fallback to unsigned thumbnails or URL
  if (thumbnail?.[preferredSize]) return thumbnail[preferredSize]!;
  if (thumbnail?.[otherSize]) return thumbnail[otherSize]!;
  
  return url || '';
};

/**
 * Normalizes an ID to a string, handling both direct strings and MongoDB $oid objects.
 */
export const normalizeId = (id: any): string => {
  if (!id) return '';
  if (typeof id === 'string') return id;
  if (typeof id === 'object' && id.$oid) return id.$oid;
  return String(id);
};
